import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { Components } from 'react-markdown'
import { cn } from '~/lib/utils'

// 챗봇·근거 등에서 공통으로 쓰는 마크다운 렌더러.
// GFM(표·체크박스·취소선) 지원. 원문에 섞인 <br>은 줄바꿈으로 정규화한다(rehype-raw 미사용 = XSS 안전).
const COMPONENTS: Components = {
    p: ({ children }) => (
        <p className='my-1.5 first:mt-0 last:mb-0'>{children}</p>
    ),
    h1: ({ children }) => (
        <h1 className='mt-3 mb-1.5 text-base font-bold first:mt-0'>
            {children}
        </h1>
    ),
    h2: ({ children }) => (
        <h2 className='mt-3 mb-1.5 text-sm font-bold first:mt-0'>{children}</h2>
    ),
    h3: ({ children }) => (
        <h3 className='mt-2 mb-1 text-sm font-semibold first:mt-0'>
            {children}
        </h3>
    ),
    ul: ({ children }) => (
        <ul className='my-1.5 ml-4 list-disc space-y-0.5'>{children}</ul>
    ),
    ol: ({ children }) => (
        <ol className='my-1.5 ml-4 list-decimal space-y-0.5'>{children}</ol>
    ),
    li: ({ children }) => <li className='leading-relaxed'>{children}</li>,
    a: ({ href, children }) => (
        <a
            href={href}
            target='_blank'
            rel='noreferrer'
            className='text-primary underline underline-offset-2'>
            {children}
        </a>
    ),
    strong: ({ children }) => (
        <strong className='font-semibold'>{children}</strong>
    ),
    blockquote: ({ children }) => (
        <blockquote className='border-border text-muted-foreground my-2 border-l-2 pl-3'>
            {children}
        </blockquote>
    ),
    hr: () => <hr className='border-border my-3' />,
    code: ({ className, children }) => {
        const block = /language-/.test(className ?? '')
        if (block) {
            return <code className='font-mono text-xs'>{children}</code>
        }
        return (
            <code className='bg-muted rounded px-1 py-0.5 font-mono text-[0.85em]'>
                {children}
            </code>
        )
    },
    pre: ({ children }) => (
        <pre className='bg-muted my-2 overflow-x-auto rounded-md p-3 text-xs'>
            {children}
        </pre>
    ),
    table: ({ children }) => (
        <div className='my-2 overflow-x-auto'>
            <table className='border-border w-full border-collapse border text-xs'>
                {children}
            </table>
        </div>
    ),
    th: ({ children }) => (
        <th className='border-border bg-muted/50 border px-2 py-1 text-left font-semibold'>
            {children}
        </th>
    ),
    td: ({ children }) => (
        <td className='border-border border px-2 py-1 align-top'>{children}</td>
    ),
}

/** 마크다운 텍스트를 앱 스타일로 렌더. */
export function Markdown({
    children,
    className,
}: {
    children: string
    className?: string
}) {
    const text = (children ?? '').replace(/<br\s*\/?>/gi, '\n')
    return (
        <div
            className={cn(
                'text-foreground text-sm leading-relaxed',
                className
            )}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTS}>
                {text}
            </ReactMarkdown>
        </div>
    )
}
